/**
 * Investment Risk Profile Questionnaire - Zod Validation Schemas
 * TASK-179: FinSA/MiFID II Compliant Risk Assessment
 * TASK-244: Standardized field naming (camelCase, aligned with KYC)
 *
 * Based on: Client_Risk_Profile_Questionnaire_Kielis.pdf (12 pages, 10 sections)
 * Frontend captures Sections 1-5 (client-filled)
 * Backend calculates Section 6 (risk profile determination)
 */

import { z } from 'zod';
import { SUB_ASSET_CLASS_VALUES, WHOLE_SAC } from '../constants/subAssetClasses';

// ============================================================================
// SHARED VALIDATORS (aligned with KYC)
// ============================================================================

/**
 * Date validation - accepts both Date objects and ISO strings (aligned with KYC PastDateSchema)
 */
const PastDateSchema = z.union([
  z.date().max(new Date(), "Date cannot be in the future"),
  z.string().datetime()
]);

/**
 * Email validation with max length (aligned with KYC EmailSchema)
 */
const EmailSchema = z.string()
  .email("Please enter a valid email address")
  .max(255);

/**
 * Swiss phone number validation (aligned with KYC SwissPhoneSchema)
 * Accepts flexible phone formats with 10-20 characters
 */
const SwissPhoneSchema = z.string()
  .regex(/^\+?[0-9\s\-()]+$/, "Please enter a valid phone number")
  .min(10)
  .max(20)
  .or(z.string().min(10).max(20)); // Allow international formats as fallback (aligned with KYC)

/**
 * Address Schema - simplified for Risk Profile (KYC uses full AddressSchema object)
 * Risk Profile uses string for simplicity, but validates similarly
 */
const AddressStringSchema = z.string()
  .min(5, "Address is required")
  .max(500);

// ============================================================================
// SECTION 1: PERSONAL INFORMATION (Pages 1-2)
// Aligned with KYC PersonalInfoSchema, ContactInfoSchema, EmploymentInfoSchema
// ============================================================================

export const personalInfoSchema = z.object({
  // Personal Identity (aligned with KYC PersonalInfoSchema)
  firstName: z.string().min(2, "First name is required").max(100),
  middleName: z.string().max(100).optional(),
  lastName: z.string().min(2, "Last name is required").max(100),
  dateOfBirth: PastDateSchema, // ALIGNED: Now accepts Date or ISO string like KYC

  // Contact Information (aligned with KYC ContactInfoSchema)
  residentialAddress: AddressStringSchema, // ALIGNED: Same validation as KYC (string format for Risk Profile)
  phonePrimary: SwissPhoneSchema, // ALIGNED: Same validation as KYC
  emailPrimary: EmailSchema, // ALIGNED: Same validation with max 255 as KYC

  // Nationality & Tax (aligned with KYC)
  nationalities: z.array(z.string().length(2)).min(1, "At least one nationality is required"), // ALIGNED: Strict ISO-2 like KYC
  taxResidence: z.string().length(2, "Tax residence must be ISO 2-letter country code"), // ALIGNED: Exact match with KYC

  // Employment (flattened, aligned with KYC EmploymentInfoSchema)
  employmentStatus: z.enum(['employed', 'selfEmployed', 'retired', 'unemployed', 'student'], {
    required_error: "Employment status is required"
  }), // ALIGNED: Subset of KYC enum
  occupation: z.string().max(255).optional(),
  employerName: z.string().max(255).optional(),

  // Family (flattened, aligned with KYC)
  maritalStatus: z.enum(['single', 'married', 'divorced', 'widowed', 'separated'], {
    required_error: "Marital status is required"
  }), // ALIGNED: Subset of KYC enum
  dependentsCount: z.number().min(0, "Cannot be negative").max(20)
});

// ============================================================================
// SECTION 2: INVESTMENT KNOWLEDGE AND EXPERIENCE (Pages 2-3)
// ============================================================================

const assetClassExperienceSchema = z.object({
  knowledge: z.enum(['none', 'basic', 'good', 'expert']),
  yearsExperience: z.number().min(0).max(60).optional(),
  transactionsLast3Years: z.number().min(0).optional()
});

export const knowledgeExperienceSchema = z.object({
  // 2.1 Education and Professional Background
  educationLevel: z.enum(['highSchool', 'bachelors', 'masters', 'phd', 'other'], {
    required_error: "Education level is required"
  }),
  fieldOfStudy: z.string().max(200).optional(),
  financeExperienceYears: z.enum(['none', '1to5', '5to10', 'moreThan10'], {
    required_error: "Finance experience is required"
  }),

  // 2.2 Investment Experience by Asset Class
  assetClassExperience: z.object({
    cashMoneyMarket: assetClassExperienceSchema,
    bonds: assetClassExperienceSchema,
    equities: assetClassExperienceSchema,
    mutualFundsEtfs: assetClassExperienceSchema,
    structuredProducts: assetClassExperienceSchema,
    derivatives: assetClassExperienceSchema,
    alternativeInvestments: assetClassExperienceSchema,
    realEstate: assetClassExperienceSchema
  }),

  // 2.3 Previous Investment Management
  usedAdvisoryServices: z.boolean(),
  previousAdvisor: z.string().max(200).optional()
});

// ============================================================================
// SECTION 3: FINANCIAL SITUATION (Pages 3-5)
// Flattened structure for simpler mapping
// ============================================================================

export const financialSituationSchema = z.object({
  // 3.1 Income (flattened)
  incomeEmploymentSalary: z.number().min(0).optional(),
  incomeSelfEmployment: z.number().min(0).optional(),
  incomeRental: z.number().min(0).optional(),
  incomePension: z.number().min(0).optional(),
  incomeInvestment: z.number().min(0).optional(),
  incomeOther: z.number().min(0).optional(),

  // 3.2 Assets (flattened)
  assetsCashDeposits: z.number().min(0).optional(),
  assetsSecuritiesInvestments: z.number().min(0).optional(),
  assetsRealEstateMarketValue: z.number().min(0).optional(),
  assetsBusinessInterests: z.number().min(0).optional(),
  assetsRetirementAccounts: z.number().min(0).optional(),
  assetsOther: z.number().min(0).optional(),

  // 3.3 Liabilities (flattened)
  liabilitiesMortgages: z.number().min(0).optional(),
  liabilitiesConsumerLoans: z.number().min(0).optional(),
  liabilitiesBusinessLoans: z.number().min(0).optional(),
  liabilitiesOtherDebts: z.number().min(0).optional(),

  // 3.4 Regular Expenses (flattened)
  expensesMonthlyLiving: z.number().min(0, "Monthly expenses required"),
  expensesAnnualExtraordinary: z.number().min(0).optional(),

  // 3.5 Portfolio Details (flattened)
  portfolioAmountToManage: z.number().min(1, "Portfolio amount is required"),
  portfolioPercentageOfNetWorth: z.number().min(0).max(100).optional(),
  portfolioSourceOfFunds: z.enum(['savings', 'inheritance', 'saleOfAssets', 'other'], {
    required_error: "Source of funds is required"
  }),
  portfolioSourceOtherDescription: z.string().max(200).optional(),

  // 3.6 Capacity to Bear Losses
  maxLossCapacity: z.enum([
    'upTo5pct',
    'upTo10pct',
    'upTo20pct',
    'upTo30pct',
    'moreThan30pct'
  ], {
    required_error: "Loss capacity is required for risk assessment"
  })
});

// ============================================================================
// SECTION 4: INVESTMENT OBJECTIVES (Page 6)
// ============================================================================

export const investmentObjectivesSchema = z.object({
  // 4.1 Investment Time Horizon
  timeHorizon: z.enum([
    'shortTerm',      // < 2 years
    'mediumTerm',     // 2-5 years
    'longTerm',       // 5-10 years
    'veryLongTerm'    // > 10 years
  ], {
    required_error: "Investment time horizon is required"
  }),

  // 4.2 Investment Purpose (Multi-select)
  investmentPurposes: z.array(z.enum([
    'capitalPreservation',
    'capitalGrowth',
    'incomeGeneration',
    'retirement',
    'estatePlanning',
    'specificGoal'
  ])).min(1, "Please select at least one investment purpose"),

  specificGoalDescription: z.string().max(500).optional(),

  // 4.3 Return Expectations
  expectedReturn: z.enum([
    'preservation',    // 0-2% p.a.
    'conservative',    // 2-4% p.a.
    'moderate',        // 4-7% p.a.
    'aggressive'       // > 7% p.a.
  ], {
    required_error: "Return expectation is required"
  }),

  // 4.4 Liquidity Needs (flattened)
  liquidityRegularWithdrawals: z.boolean(),
  liquidityWithdrawalFrequency: z.enum(['monthly', 'quarterly', 'annually', 'none']).optional(),
  liquidityWithdrawalAmount: z.number().min(0).optional(),
  liquidityAnticipatedWithdrawal3yrs: z.boolean(),
  liquidityAnticipatedAmount: z.number().min(0).optional(),
  liquidityAnticipatedYear: z.number().min(1).max(3).optional()
});

// ============================================================================
// SECTION 5: RISK TOLERANCE (Pages 7-8)
// ============================================================================

export const riskToleranceSchema = z.object({
  // 5.1 Risk Attitude
  riskAttitude: z.enum([
    'veryConservative',
    'conservative',
    'balanced',
    'growthOriented',
    'aggressive'
  ], {
    required_error: "Risk attitude is required"
  }),

  // 5.2 Risk Perception (Behavioral Question)
  portfolioDeclineReaction: z.enum([
    'sellImmediately',
    'sellSome',
    'holdRecovery',
    'buyMore'
  ], {
    required_error: "Please indicate your reaction to portfolio decline"
  }),

  // 5.3 Maximum Acceptable Loss
  maxAcceptableLoss1yr: z.enum([
    'upTo5pct',
    'upTo10pct',
    'upTo20pct',
    'upTo30pct',
    'moreThan30pct'
  ], {
    required_error: "Maximum acceptable loss is required"
  }),

  // 5.4 Investment Restrictions (flattened)
  restrictionsHasRestrictions: z.boolean(),
  restrictionsEsgCriteria: z.boolean().optional(),
  restrictionsEsgSpecify: z.string().max(500).optional(),
  restrictionsEthicalReligious: z.boolean().optional(),
  restrictionsEthicalSpecify: z.string().max(500).optional(),
  restrictionsSectorExclusions: z.boolean().optional(),
  restrictionsSectorsExclude: z.array(z.string()).optional(),
  restrictionsGeographic: z.boolean().optional(),
  restrictionsGeographicSpecify: z.string().max(500).optional(),
  restrictionsOther: z.boolean().optional(),
  restrictionsOtherSpecify: z.string().max(500).optional()
});

// ============================================================================
// SECTION 6: DETERMINED RISK PROFILE (Page 8)
// Advisor-Filled | Calculated by System
// ============================================================================

export const determinedRiskProfileSchema = z.object({
  determinedProfile: z.enum([
    'veryConservative',
    'conservative',
    'balanced',
    'growthOriented',
    'aggressive'
  ], {
    required_error: "Risk profile must be determined"
  }),

  characteristicsExpectedAnnualReturnPct: z.number().min(0).max(100),
  characteristicsExpectedVolatilityPct: z.number().min(0).max(100),
  characteristicsMaxExpectedDrawdownPct: z.number().min(0).max(100)
});

// ============================================================================
// SECTION 7: RECOMMENDED INVESTMENT STRATEGY (Page 9)
// ============================================================================

// TASK-471: strategic allocation slot at sub-asset-class granularity (3-key composite).
// industryGroup/region default to the WHOLE_SAC sentinel ("*") — a whole-SAC target the EAM
// can later refine into industry/region sub-targets (used per-instrument by the swap engine).
export const targetAllocationSlotSchema = z.object({
  assetClass: z.string().min(1),
  subAssetClass: z.enum(SUB_ASSET_CLASS_VALUES),
  industryGroup: z.string().default(WHOLE_SAC),
  region: z.string().default(WHOLE_SAC),
  targetWeightPct: z.number().min(0).max(100),
});

export const recommendedStrategySchema = z.object({
  strategyName: z.string().min(1, "Strategy name is required").max(200),

  targetAllocations: z
    .array(targetAllocationSlotSchema)
    .refine(
      (slots) => Math.abs(slots.reduce((sum, s) => sum + (s.targetWeightPct || 0), 0) - 100) < 0.01,
      { message: "Target allocations must sum to 100%" }
    ),
  driftTolerancePct: z.number().min(0).default(2.0),

  investmentApproach: z.enum(['passive', 'active', 'combination'], {
    required_error: "Investment approach is required"
  }),

  geographicAllocation: z.enum(['domestic', 'global', 'regional'], {
    required_error: "Geographic allocation is required"
  }),

  regionalFocus: z.string().max(200).optional(),
  rebalancingFrequency: z.string().min(1, "Rebalancing frequency is required").max(100)
});

// ============================================================================
// SECTION 8: DECLARATIONS AND ACKNOWLEDGMENTS (Pages 9-10)
// ============================================================================

export const declarationsSchema = z.object({
  declarationInformationAccurate: z.boolean().refine(val => val === true, {
    message: "You must confirm that all information is accurate"
  }),
  declarationUnderstandsRecommendations: z.boolean().refine(val => val === true, {
    message: "You must acknowledge that recommendations are based on provided information"
  }),
  declarationInformedAboutRisks: z.boolean().refine(val => val === true, {
    message: "You must acknowledge being informed about investment risks"
  }),
  declarationInformedAboutCosts: z.boolean().refine(val => val === true, {
    message: "You must acknowledge being informed about costs and fees"
  }),
  declarationUnderstandsPastPerformance: z.boolean().refine(val => val === true, {
    message: "You must acknowledge that past performance does not indicate future results"
  }),
  declarationAcknowledgesFluctuation: z.boolean().refine(val => val === true, {
    message: "You must acknowledge that investment values can fluctuate"
  }),
  declarationAgreesToInformChanges: z.boolean().refine(val => val === true, {
    message: "You must agree to inform about material changes"
  }),

  profileReviewedRiskProfile: z.boolean().refine(val => val === true, {
    message: "You must review and agree with the determined risk profile"
  }),
  profileReviewedStrategy: z.boolean().refine(val => val === true, {
    message: "You must review and agree with the recommended strategy"
  }),
  profileUnderstandsStrategyMatch: z.boolean().refine(val => val === true, {
    message: "You must acknowledge understanding of the strategy match"
  }),

  alternativeRequestsAlternative: z.boolean(),
  alternativeRequestedStrategy: z.string().max(500).optional(),
  alternativeAcknowledgesMismatch: z.boolean().optional()
});

// ============================================================================
// SECTION 9: SIGNATURES (Page 11)
// ============================================================================

export const signaturesSchema = z.object({
  clientSignatureName: z.string().min(2, "Client name is required").max(200),
  clientSignatureData: z.string().min(1, "Client signature is required"),
  clientSignatureDate: z.date({ required_error: "Signature date is required" }),
  clientSignatureSigned: z.boolean(),

  advisorSignatureName: z.string().min(2, "Advisor name is required").max(200),
  advisorSignatureData: z.string().optional(),
  advisorSignatureDate: z.date().optional(),
  advisorSignatureLicenseNumber: z.string().max(100).optional(),
  advisorSignatureSigned: z.boolean().optional(),

  /**
   * TASK-931 §2: the SIGNATURE ARTIFACT — the signed PDF's document id, written server-side by
   * `/extract` when the document lands. The booleans above derive from this: `/complete`
   * returns 409 if a signature is claimed without it, so "signed" can no longer be a checkbox
   * with nothing behind it.
   */
  signedPdfDocumentId: z.string().optional(),
  signedPdfUploadedAt: z.string().optional()
});

// ============================================================================
// SECTION 10: PERIODIC REVIEW (Pages 11-12)
// ============================================================================

export const periodicReviewSchema = z.object({
  reviewFrequency: z.string().min(1, "Review frequency is required"),
  nextReviewDate: z.date({ required_error: "Next review date is required" }),
  reviewTriggers: z.array(z.string()).default([
    "At least every 2-3 years",
    "When there are significant changes in personal or financial circumstances",
    "When investment objectives change",
    "Upon client request"
  ])
});

// ============================================================================
// COMPLETE RISK PROFILE SCHEMA (All 10 Sections)
// ============================================================================

export const completeRiskProfileSchema = z.object({
  section1: personalInfoSchema,
  section2: knowledgeExperienceSchema,
  section3: financialSituationSchema,
  section4: investmentObjectivesSchema,
  section5: riskToleranceSchema,
  section6: determinedRiskProfileSchema,
  section7: recommendedStrategySchema,
  section8: declarationsSchema,
  section9: signaturesSchema,
  section10: periodicReviewSchema
});

// ============================================================================
// TYPESCRIPT TYPES (Inferred from Schemas)
// ============================================================================

export type PersonalInfo = z.infer<typeof personalInfoSchema>;
export type KnowledgeExperience = z.infer<typeof knowledgeExperienceSchema>;
export type FinancialSituation = z.infer<typeof financialSituationSchema>;
export type InvestmentObjectives = z.infer<typeof investmentObjectivesSchema>;
export type RiskTolerance = z.infer<typeof riskToleranceSchema>;
export type DeterminedRiskProfile = z.infer<typeof determinedRiskProfileSchema>;
export type RecommendedStrategy = z.infer<typeof recommendedStrategySchema>;
export type Declarations = z.infer<typeof declarationsSchema>;
export type Signatures = z.infer<typeof signaturesSchema>;
export type PeriodicReview = z.infer<typeof periodicReviewSchema>;
export type CompleteRiskProfile = z.infer<typeof completeRiskProfileSchema>;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate total annual income
 */
export const calculateTotalIncome = (financial: FinancialSituation): number => {
  return (
    (financial.incomeEmploymentSalary || 0) +
    (financial.incomeSelfEmployment || 0) +
    (financial.incomeRental || 0) +
    (financial.incomePension || 0) +
    (financial.incomeInvestment || 0) +
    (financial.incomeOther || 0)
  );
};

/**
 * Calculate total assets
 */
export const calculateTotalAssets = (financial: FinancialSituation): number => {
  return (
    (financial.assetsCashDeposits || 0) +
    (financial.assetsSecuritiesInvestments || 0) +
    (financial.assetsRealEstateMarketValue || 0) +
    (financial.assetsBusinessInterests || 0) +
    (financial.assetsRetirementAccounts || 0) +
    (financial.assetsOther || 0)
  );
};

/**
 * Calculate total liabilities
 */
export const calculateTotalLiabilities = (financial: FinancialSituation): number => {
  return (
    (financial.liabilitiesMortgages || 0) +
    (financial.liabilitiesConsumerLoans || 0) +
    (financial.liabilitiesBusinessLoans || 0) +
    (financial.liabilitiesOtherDebts || 0)
  );
};

/**
 * Calculate net worth from financial situation data
 */
export const calculateNetWorth = (financial: FinancialSituation): number => {
  return calculateTotalAssets(financial) - calculateTotalLiabilities(financial);
};

/**
 * Determine risk capacity from financial situation
 */
export const determineRiskCapacity = (financial: FinancialSituation): 'low' | 'medium' | 'high' => {
  const netWorth = calculateNetWorth(financial);
  const portfolioAmount = financial.portfolioAmountToManage;
  const portfolioRatio = netWorth > 0 ? (portfolioAmount / netWorth) : 1;

  if (
    netWorth > 5000000 &&
    portfolioRatio < 0.5 &&
    ['upTo30pct', 'moreThan30pct'].includes(financial.maxLossCapacity)
  ) {
    return 'high';
  }

  if (
    netWorth < 1000000 ||
    portfolioRatio > 0.8 ||
    ['upTo5pct', 'upTo10pct'].includes(financial.maxLossCapacity)
  ) {
    return 'low';
  }

  return 'medium';
};

/**
 * Map asset class knowledge to overall knowledge level
 */
export const determineKnowledgeLevel = (knowledge: KnowledgeExperience): 'novice' | 'intermediate' | 'experienced' | 'expert' => {
  const assetClasses = Object.values(knowledge.assetClassExperience);
  const expertCount = assetClasses.filter(ac => ac.knowledge === 'expert').length;
  const goodCount = assetClasses.filter(ac => ac.knowledge === 'good').length;

  if (expertCount >= 4 || knowledge.financeExperienceYears === 'moreThan10') return 'expert';
  if (expertCount >= 2 || goodCount >= 4 || knowledge.financeExperienceYears === '5to10') return 'experienced';
  if (goodCount >= 2 || knowledge.financeExperienceYears === '1to5') return 'intermediate';
  return 'novice';
};

/**
 * Calculate overall risk profile (Section 6)
 */
export const calculateDeterminedRiskProfile = (
  financial: FinancialSituation,
  objectives: InvestmentObjectives,
  tolerance: RiskTolerance
): 'veryConservative' | 'conservative' | 'balanced' | 'growthOriented' | 'aggressive' => {
  const riskCapacity = determineRiskCapacity(financial);
  const riskAttitude = tolerance.riskAttitude;
  const timeHorizon = objectives.timeHorizon;

  const capacityScore = { low: 1, medium: 2, high: 3 }[riskCapacity];
  const attitudeScore = {
    veryConservative: 1,
    conservative: 2,
    balanced: 3,
    growthOriented: 4,
    aggressive: 5
  }[riskAttitude];

  let baseScore = Math.min(capacityScore, Math.floor(attitudeScore / 1.5));

  if (timeHorizon === 'veryLongTerm') baseScore = Math.min(baseScore + 1, 5);
  else if (timeHorizon === 'shortTerm') baseScore = Math.max(baseScore - 1, 1);

  if (baseScore <= 1) return 'veryConservative';
  if (baseScore === 2) return 'conservative';
  if (baseScore === 3) return 'balanced';
  if (baseScore === 4) return 'growthOriented';
  return 'aggressive';
};

